<template>
  <Component
    :is="maxSmall ? Drawer : Dialog"
    :open="open"
    @update:open="$emit('update:open', $event)"
    size="full"
    v-bind="maxSmall ? {} : { side: 'default' }"
    :teleport="maxSmall"
    showFooter
  >
    <slot />
    <template #header>
      <div v-if="maxSmall" class="flex flex-row justify-between items-center">
        <DrawerHeader
          :title="$t('components.profile.edit.title')"
          type="gray"
          class="w-full bg-primary border-b border-secondary"
          variant="profile"
        />
      </div>
      <DialogHeader
        v-else
        :title="$t('components.profile.edit.title')"
        :description="$t('components.profile.edit.description')"
        class="p-6 pb-5 bg-primary border-b border-secondary"
      />
    </template>
    <template #content>
      <div class="flex flex-col grow px-6 pt-4 bg-primary overflow-y-auto">
        <div
          class="flex border-b border-secondary pb-4"
          :class="{
            'justify-center': maxSmall,
            'justify-start': !maxSmall,
          }"
        >
          <AvatarInput
            :image="user.avatar_url"
            @fileSelected="handleFileSelected($event)"
            @fileRemoved="$emit('fileRemoved')"
          />
        </div>
        <div class="flex flex-col pt-4 pb-3 gap-1 justify-start text-left">
          <span
            class="mb-2 font-semibold"
            :class="{
              'text-style-sm': !maxSmall,
            }"
          >
            {{ $t("components.profile.edit.section.contact_info") }}
          </span>
          <TextInput
            :label="$t('components.profile.edit.first_name.label')"
            v-model="data.firstname"
            :errorMessage="errors.firstname?.[0]"
            :size="maxSmall ? 'lg' : 'sm'"
          />
          <TextInput
            :label="$t('components.profile.edit.last_name.label')"
            v-model="data.lastname"
            :errorMessage="errors.lastname?.[0]"
            :size="maxSmall ? 'lg' : 'sm'"
          />
          <TextInput
            :label="$t('components.profile.edit.phone.label')"
            v-model="data.phone"
            :errorMessage="errors.phone?.[0]"
            :size="maxSmall ? 'lg' : 'sm'"
          />
        </div>
      </div>
    </template>
    <template #footer>
      <div class="flex flex-col bg-primary py-4 px-4 md:px-6 gap-2 md:mt-auto">
        <div class="flex items-end gap-2">
          <Button
            v-if="!maxSmall"
            :label="$t('components.buttons.cancel')"
            size="xl"
            @click="$emit('update:open', false)"
            class="w-full"
            variant="secondary"
            type="gray"
          />
          <Button
            :label="$t('components.buttons.confirm')"
            size="xl"
            class="w-full"
            @click="saveProfile"
            :disabled="busy || noChanges"
          />
        </div>
      </div>
    </template>
  </Component>
</template>

<script setup>
import { ref, computed, watchEffect } from "vue";
import { AvatarInput, TextInput } from "@/SharedComponents/Inputs";
import { Drawer, DrawerHeader } from "@/SharedComponents/Drawer";
import { Dialog, DialogHeader } from "@/SharedComponents/Dialog";
import { Button } from "@/SharedComponents/Buttons";
import { useBreakpoints } from "@/Composables/useBreakpoints";

const { maxSmall } = useBreakpoints();

const emit = defineEmits([
  "saveProfile",
  "update:open",
  "fileSelected",
  "fileRemoved",
]);

const props = defineProps({
  open: { type: Boolean, default: undefined },
  user: { type: Object, required: true },
  errors: { type: Object, default: () => ({}) },
  busy: { type: Boolean, default: false },
});

const data = ref({ ...props.user });
watchEffect(() => {
  if (props.open) {
    data.value = { ...props.user };
  }
});

const saveProfile = () => {
  emit("clearErrors");
  emit("saveProfile", {
    data: {
      avatar_url: data.value.avatar_url,
      firstname: data.value.firstname,
      lastname: data.value.lastname,
      phone: data.value.phone,
    },
    callback: () => emit("update:open", false),
  });
};

const handleFileSelected = (file) => {
  emit("fileSelected", file);
  data.value.avatar_url = "newFile";
};

const noChanges = computed(() => {
  return (
    data.value.avatar_url === props.user.avatar_url &&
    data.value.firstname === props.user.firstname &&
    data.value.lastname === props.user.lastname &&
    data.value.phone === props.user.phone
  );
});
</script>
